import * as React from 'react'
import { clsx } from 'clsx'

import { useControllableState } from '../../hooks/useControllableState'
import { useMediaQuery } from '../../hooks/useMediaQuery'
import { useRovingGrid } from '../../hooks/useRovingGrid'
import { Drawer, useIsInDrawer } from '../Drawer'
import { Popover } from '../Popover'
import type { PopoverContentProps, PopoverProps } from '../Popover'
import { VisuallyHidden } from '../VisuallyHidden'
import { CheckIcon, XIcon, ShuffleIcon } from '../icons'

import { ICON_META_ALL, ICON_NAMES } from './iconName'
import type { IconName } from './iconName'
import { DEFAULT_ICON_COLOR, ICON_COLOR_LABELS, ICON_COLORS } from './constants'
import type { IconColorName } from './constants'
import { PickerIcon } from './PickerIcon'
import { useIcons } from './useIcons'
import type { IconsModule } from './icons'

import styles from './IconPicker.module.css'

const DEFAULT_COLUMNS = 8

/** Below this viewport width the picker opens as a bottom drawer, not a popover. */
const DRAWER_MEDIA_QUERY = '(width < 640px)'

/** Inline style carrying the grid column-count as a CSS custom property. */
interface GridCssProperties extends React.CSSProperties {
  '--icon-picker-columns'?: number
}

/** Selected icon plus its tint color. */
type IconPickerValue = {
  icon: IconName
  color: IconColorName
}

/**
 * Shared state for the IconPicker compound components.
 *
 * @internal
 */
type IconPickerContextValue = {
  value: IconPickerValue
  /** Whether the picker is open — drives the search combobox's `aria-expanded`. */
  open: boolean
  /** Commits the final selection and closes the popover. */
  selectIcon: (icon: IconName) => void
  /** Re-tints the current selection without closing. */
  selectColor: (color: IconColorName) => void
  search: string
  setSearch: (search: string) => void
  /** Icon names matching the current search query, in curated order. */
  filteredIcons: readonly IconName[]
  /** Resolved icon components once the lazy chunk has loaded; null while pending. */
  icons: IconsModule | null
  /** Set when the lazy icon chunk fails to load, surfacing a retry affordance. */
  iconsError: boolean
  /** Clears the error and re-attempts loading the lazy icon chunk. */
  retryLoadIcons: () => void
  columns: number
  /** Injected RNG (defaults to Math.random) so the shuffle is testable. */
  random: () => number
  searchRef: React.MutableRefObject<HTMLInputElement | null>
  instructionsId: string
  /** True once the user has navigated with the keyboard; gates scroll-into-view. */
  hasNavigatedRef: React.MutableRefObject<boolean>
  /**
   * Index of the virtually-focused grid cell. Focus stays on the search input;
   * this drives the cell's highlight and the input's `aria-activedescendant`.
   */
  activeIndex: number
  /**
   * Keydown handler for the search input: arrow/Home/End move the active cell,
   * Enter commits it. Everything else (typing, backspace) edits the query, so
   * navigation and filtering share one focus point.
   */
  handleSearchKeyDown: (event: React.KeyboardEvent) => void
  /** DOM id of the active cell, for the search input's `aria-activedescendant`. */
  activeDescendantId: string | undefined
  /** DOM id of the grid, referenced by the search input's `aria-controls`. */
  gridId: string
  /** Builds the stable DOM id for the cell at a given filtered index. */
  cellId: (index: number) => string
}

const IconPickerContext = React.createContext<IconPickerContextValue | null>(
  null,
)

/**
 * Reads the IconPicker compound context.
 *
 * @internal
 */
function useIconPickerContext(): IconPickerContextValue {
  const context = React.useContext(IconPickerContext)
  if (!context) {
    throw new Error(
      'IconPicker compound components must be used within IconPicker',
    )
  }
  return context
}

function matchesQuery(icon: IconName, query: string): boolean {
  if (icon.includes(query)) return true
  return ICON_META_ALL[icon].some((keyword) => keyword.includes(query))
}

/** Splits a list into fixed-size rows for the grid's `role="row"` wrappers. */
function chunk<T>(items: readonly T[], size: number): T[][] {
  const rows: T[][] = []
  for (let index = 0; index < items.length; index += size) {
    rows.push(items.slice(index, index + size))
  }
  return rows
}

/**
 * Props for the IconPicker root.
 */
type IconPickerProps = Pick<
  PopoverProps,
  'open' | 'defaultOpen' | 'onOpenChange' | 'modal'
> & {
  /** Compound children (Trigger and Content). */
  children: React.ReactNode
  /** Controlled selection. Pair with `onValueChange`. */
  value?: IconPickerValue
  /** Initial selection when uncontrolled. */
  defaultValue?: IconPickerValue
  /** Called with the new `{ icon, color }` when a selection is committed. */
  onValueChange?: (value: IconPickerValue) => void
  /**
   * Number of columns in the icon grid.
   * @default 8
   */
  columns?: number
  /**
   * RNG used by the shuffle action. Injectable for deterministic tests.
   * @default Math.random
   */
  random?: () => number
}

/** Fallback selection when the picker is uncontrolled and given no default. */
const DEFAULT_ICON_PICKER_VALUE: IconPickerValue = {
  icon: ICON_NAMES[0],
  color: DEFAULT_ICON_COLOR,
}

const IconPicker = React.forwardRef<HTMLDivElement, IconPickerProps>(
  (
    {
      children,
      value: valueProp,
      defaultValue,
      onValueChange,
      open: openProp,
      defaultOpen,
      onOpenChange,
      modal,
      columns: columnsProp = DEFAULT_COLUMNS,
      random = Math.random,
    },
    ref,
  ) => {
    const [value, setValue] = useControllableState<IconPickerValue>({
      prop: valueProp,
      defaultProp: defaultValue ?? DEFAULT_ICON_PICKER_VALUE,
      onChange: onValueChange,
    })
    const [open, setOpen] = useControllableState<boolean>({
      prop: openProp,
      defaultProp: defaultOpen ?? false,
      onChange: onOpenChange,
    })
    const [search, setSearch] = React.useState('')
    // Load the icon geometry as a code-split chunk on mount (not gated on open)
    // so the closed trigger can render its selected glyph; a load failure
    // surfaces a retry affordance instead of an endless skeleton.
    const { icons, error: iconsError, retry: retryLoadIcons } = useIcons()
    const searchRef = React.useRef<HTMLInputElement | null>(null)
    const instructionsId = React.useId()
    // True once the user has moved the active cell with the keyboard this open
    // session — gates the scroll-into-view so opening never jumps the page.
    const hasNavigatedRef = React.useRef(false)

    // The current value always has a concrete shape; useControllableState's
    // value can be undefined in its type, so fall back defensively.
    const currentValue = value ?? DEFAULT_ICON_PICKER_VALUE

    // Guard against 0/negative column counts, which would make the grid's
    // fixed-size chunking loop forever.
    const columns = Math.max(1, columnsProp)

    // On narrow viewports the picker opens as a bottom drawer instead of a
    // popover; the compound parts read `useIsInDrawer()` to render their
    // drawer variants.
    const shouldBeDrawer = useMediaQuery(DRAWER_MEDIA_QUERY)

    const filteredIcons = React.useMemo(() => {
      const query = search.trim().toLowerCase()
      if (!query) return ICON_NAMES
      return ICON_NAMES.filter((icon) => matchesQuery(icon, query))
    }, [search])

    const selectIcon = React.useCallback(
      (icon: IconName): void => {
        setValue((previous) => ({
          icon,
          color: (previous ?? DEFAULT_ICON_PICKER_VALUE).color,
        }))
        setOpen(false)
        // Match handleOpenChange: a committed selection also clears the search
        // so the next open starts from the full set.
        setSearch('')
      },
      [setValue, setOpen],
    )

    // Roving-grid state lives here (not in the Grid) so the search input can
    // drive navigation while keeping DOM focus: arrow keys move a *virtual*
    // active cell, surfaced via the input's `aria-activedescendant`.
    const gridId = React.useId()
    const cellId = React.useCallback(
      (index: number): string => `${gridId}-cell-${index}`,
      [gridId],
    )
    const {
      activeIndex,
      setActiveIndex,
      onKeyDown: onGridKeyDown,
    } = useRovingGrid({
      itemCount: filteredIcons.length,
      columns,
    })
    // Only advertise the active descendant while the grid is actually rendered
    // (not the skeleton, error, or empty state), so the reference never dangles.
    const isGridRendered =
      Boolean(icons) && !iconsError && filteredIcons.length > 0
    const activeDescendantId = isGridRendered ? cellId(activeIndex) : undefined

    // On open, start the active cell on the committed icon so its tinted tile is
    // the one highlighted; as the user navigates, the tint follows the active
    // cell (only ever one cell is tinted).
    React.useEffect(() => {
      if (!open) return
      // Fresh session: the reposition below shouldn't count as navigation, so
      // it doesn't scroll the page on open.
      hasNavigatedRef.current = false
      const committedIndex = filteredIcons.indexOf(currentValue.icon)
      setActiveIndex(committedIndex >= 0 ? committedIndex : 0)
      // Reposition only when the popover opens, not on every filter keystroke.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    const handleSearchKeyDown = React.useCallback(
      (event: React.KeyboardEvent): void => {
        // Enter commits the active cell. Space is intentionally left alone so it
        // types into the query; only Enter selects.
        if (event.key === 'Enter') {
          event.preventDefault()
          const icon = filteredIcons[activeIndex]
          if (icon) selectIcon(icon)
          return
        }
        // Arrow/Home/End navigate the grid; the hook ignores every other key,
        // so typing and backspace fall through to edit the search query. Mark
        // navigation so the active cell may now scroll into view.
        if (
          event.key.startsWith('Arrow') ||
          event.key === 'Home' ||
          event.key === 'End'
        ) {
          hasNavigatedRef.current = true
        }
        onGridKeyDown(event)
      },
      [filteredIcons, activeIndex, selectIcon, onGridKeyDown],
    )

    const selectColor = React.useCallback(
      (color: IconColorName): void => {
        setValue((previous) => ({
          icon: (previous ?? DEFAULT_ICON_PICKER_VALUE).icon,
          color,
        }))
      },
      [setValue],
    )

    const handleOpenChange = React.useCallback(
      (nextOpen: boolean): void => {
        setOpen(nextOpen)
        if (!nextOpen) setSearch('')
      },
      [setOpen],
    )

    const contextValue = React.useMemo<IconPickerContextValue>(
      () => ({
        value: currentValue,
        open: open ?? false,
        selectIcon,
        selectColor,
        search,
        setSearch,
        filteredIcons,
        icons,
        iconsError,
        retryLoadIcons,
        columns,
        random,
        searchRef,
        instructionsId,
        hasNavigatedRef,
        activeIndex,
        handleSearchKeyDown,
        activeDescendantId,
        gridId,
        cellId,
      }),
      [
        currentValue,
        open,
        selectIcon,
        selectColor,
        search,
        filteredIcons,
        icons,
        iconsError,
        retryLoadIcons,
        columns,
        random,
        instructionsId,
        activeIndex,
        handleSearchKeyDown,
        activeDescendantId,
        gridId,
        cellId,
      ],
    )

    const surface = (
      <div ref={ref} className={styles.root}>
        {children}
      </div>
    )

    return (
      <IconPickerContext.Provider value={contextValue}>
        {shouldBeDrawer ? (
          <Drawer
            direction="bottom"
            open={open}
            onOpenChange={handleOpenChange}
            modal={modal}
          >
            {surface}
          </Drawer>
        ) : (
          <Popover open={open} onOpenChange={handleOpenChange} modal={modal}>
            {surface}
          </Popover>
        )}
      </IconPickerContext.Provider>
    )
  },
)

IconPicker.displayName = 'IconPicker'

function tintClassName(color: IconColorName): string {
  // Every IconColorName has a matching CSS modifier class.
  return styles[color]
}

/**
 * Button that opens the picker, showing the current selection on a tinted tile.
 */
type IconPickerTriggerProps = Omit<
  React.ComponentPropsWithoutRef<'button'>,
  'children'
>

const IconPickerTrigger = React.forwardRef<
  HTMLButtonElement,
  IconPickerTriggerProps
>(({ className, ...props }, ref) => {
  const { value, icons } = useIconPickerContext()
  // Popover and Drawer both expose an `asChild` trigger; pick the one matching
  // the surface the root rendered.
  const TriggerWrapper = useIsInDrawer() ? Drawer.Trigger : Popover.Trigger

  return (
    <TriggerWrapper>
      <button
        type="button"
        ref={ref}
        className={clsx(styles.trigger, tintClassName(value.color), className)}
        aria-label="Change icon"
        {...props}
      >
        <span className={styles.triggerGlyph}>
          <PickerIcon icon={value.icon} icons={icons} size={20} />
        </span>
      </button>
    </TriggerWrapper>
  )
})

IconPickerTrigger.displayName = 'IconPicker.Trigger'

/**
 * Popover surface hosting the search, swatches, shuffle, and icon grid.
 */
type IconPickerContentProps = Pick<
  PopoverContentProps,
  | 'side'
  | 'align'
  | 'sideOffset'
  | 'alignOffset'
  | 'className'
  | 'id'
  | 'onEscapeKeyDown'
  | 'onPointerDownOutside'
>

const IconPickerContent = React.forwardRef<
  HTMLDivElement,
  IconPickerContentProps
>(
  (
    {
      side = 'bottom',
      align = 'start',
      sideOffset,
      alignOffset,
      className,
      ...props
    },
    ref,
  ) => {
    const { icons, iconsError, filteredIcons, searchRef, instructionsId } =
      useIconPickerContext()
    const isInDrawer = useIsInDrawer()

    const instructions = (
      <VisuallyHidden id={instructionsId}>
        Use the arrow keys to browse icons, then press Enter to select an icon.
      </VisuallyHidden>
    )

    const body = (
      <>
        <div className={styles.header}>
          <IconPickerSearch />
          <IconPickerShuffle />
        </div>
        <IconPickerSwatches />
        <div className={styles.scrollArea}>
          {iconsError ? (
            <IconPickerError />
          ) : icons && filteredIcons.length === 0 ? (
            <IconPickerEmpty />
          ) : (
            <IconPickerGrid />
          )}
        </div>
      </>
    )

    if (isInDrawer) {
      return (
        <Drawer.Content
          ref={ref}
          className={className}
          // Don't auto-focus the search input on mobile: it would pop the
          // on-screen keyboard over the grid the moment the drawer opens.
          onOpenAutoFocus={(event) => event.preventDefault()}
          {...props}
        >
          <VisuallyHidden>
            <Drawer.Title>Choose an icon</Drawer.Title>
            <Drawer.Description>
              Search and pick an icon and a color.
            </Drawer.Description>
          </VisuallyHidden>
          <div className={styles.drawerBody}>
            {instructions}
            {body}
          </div>
        </Drawer.Content>
      )
    }

    return (
      <Popover.Content
        ref={ref}
        side={side}
        align={align}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        aria-label="Icon picker"
        className={clsx(styles.content, className)}
        onOpenAutoFocus={(event) => {
          // Move focus to search rather than the first grid cell, matching the
          // EmojiPicker so typing starts filtering immediately.
          event.preventDefault()
          searchRef.current?.focus()
        }}
        {...props}
      >
        {instructions}
        {body}
      </Popover.Content>
    )
  },
)

IconPickerContent.displayName = 'IconPicker.Content'

/**
 * Search field that filters icons by name and keyword alias.
 */
type IconPickerSearchProps = {
  /**
   * Placeholder text.
   * @default 'Search'
   */
  placeholder?: string
}

const IconPickerSearch = React.forwardRef<
  HTMLInputElement,
  IconPickerSearchProps
>(({ placeholder = 'Search' }, ref) => {
  const {
    open,
    search,
    setSearch,
    searchRef,
    handleSearchKeyDown,
    activeDescendantId,
    gridId,
    instructionsId,
  } = useIconPickerContext()

  const setRefs = React.useCallback(
    (node: HTMLInputElement | null): void => {
      searchRef.current = node
      if (typeof ref === 'function') ref(node)
      else if (ref) ref.current = node
    },
    [ref, searchRef],
  )

  const handleClear = React.useCallback((): void => {
    setSearch('')
    requestAnimationFrame(() => searchRef.current?.focus())
  }, [setSearch, searchRef])

  return (
    <div className={styles.searchWrapper}>
      {/*
        The input keeps focus and drives the grid via a virtual active cell, so
        it's an ARIA combobox with a grid popup: aria-controls + aria-activedescendant
        point screen readers at the active cell as arrow keys move it. Both refs
        are only advertised while the grid is actually rendered so they never dangle.

        no-redundant-roles is a false positive here: a bare <input type="text"> has
        an implicit role of `textbox`, not `combobox` (the combobox role is what makes
        AT announce the active descendant), so the explicit role is required.
      */}
      {/* eslint-disable jsx-a11y/no-redundant-roles */}
      <input
        ref={setRefs}
        type="text"
        className={styles.searchInput}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        onKeyDown={handleSearchKeyDown}
        placeholder={placeholder}
        aria-label="Search icons"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="grid"
        aria-autocomplete="list"
        aria-controls={activeDescendantId ? gridId : undefined}
        aria-activedescendant={activeDescendantId}
        aria-describedby={instructionsId}
      />
      {/* eslint-enable jsx-a11y/no-redundant-roles */}
      {search !== '' && (
        <button
          type="button"
          className={styles.clearButton}
          onClick={handleClear}
          aria-label="Clear search"
        >
          <XIcon size="small" aria-hidden />
        </button>
      )}
    </div>
  )
})

IconPickerSearch.displayName = 'IconPicker.Search'

/**
 * Single-select color swatches that re-tint the current selection.
 */
type IconPickerSwatchesProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onChange'
>

const IconPickerSwatches = React.forwardRef<
  HTMLDivElement,
  IconPickerSwatchesProps
>(({ className, ...props }, ref) => {
  const { value, selectColor } = useIconPickerContext()
  const groupName = React.useId()

  // Rendered directly (rather than via SwatchGroup) because the swatch value is
  // an IconColorName tinted through CSS custom properties, not a hex/named
  // TagColor that SwatchGroup would resolve; `greenDark` in particular has no
  // TagColor equivalent.
  return (
    <div
      ref={ref}
      role="radiogroup"
      aria-label="Icon color"
      className={clsx(styles.swatches, className)}
      {...props}
    >
      {ICON_COLORS.map((color) => {
        const selected = value.color === color
        return (
          <label key={color} className={styles.swatchLabel}>
            <VisuallyHidden>{ICON_COLOR_LABELS[color]}</VisuallyHidden>
            <input
              type="radio"
              name={groupName}
              value={color}
              checked={selected}
              onChange={() => selectColor(color)}
              className={styles.swatchInput}
            />
            <span className={clsx(styles.swatch, tintClassName(color))}>
              {selected && <CheckIcon size="xsmall" color="currentColor" />}
            </span>
          </label>
        )
      })}
    </div>
  )
})

IconPickerSwatches.displayName = 'IconPicker.Swatches'

/**
 * Selects a random icon from the currently filtered set.
 */
type IconPickerShuffleProps = Omit<
  React.ComponentPropsWithoutRef<'button'>,
  'children' | 'onClick'
>

const IconPickerShuffle = React.forwardRef<
  HTMLButtonElement,
  IconPickerShuffleProps
>(({ className, ...props }, ref) => {
  const { filteredIcons, random, selectIcon } = useIconPickerContext()

  const handleShuffle = React.useCallback((): void => {
    if (filteredIcons.length === 0) return
    // Clamp so an injected random() returning >=1, negative, or NaN can't index
    // past the set; the guard covers the NaN case that clamping alone can't.
    const raw = Math.floor(random() * filteredIcons.length)
    const icon =
      filteredIcons[Math.min(Math.max(raw, 0), filteredIcons.length - 1)]
    if (icon) selectIcon(icon)
  }, [filteredIcons, random, selectIcon])

  return (
    <button
      type="button"
      ref={ref}
      className={clsx(styles.shuffle, className)}
      onClick={handleShuffle}
      disabled={filteredIcons.length === 0}
      aria-label="Shuffle icon"
      {...props}
    >
      <ShuffleIcon size="small" aria-hidden />
    </button>
  )
})

IconPickerShuffle.displayName = 'IconPicker.Shuffle'

type IconPickerGridCellProps = {
  icon: IconName
  /** Loaded glyph map, used by curated glyph icons (grid only renders once set). */
  icons: IconsModule
  /** Stable DOM id, targeted by the search input's `aria-activedescendant`. */
  id: string
  /**
   * Whether this is the active (virtually-focused) cell — the combobox's active
   * descendant. Drives both the tinted tile and `aria-selected`, so the option
   * a screen reader announces matches the one that Enter will commit.
   */
  isActive: boolean
  onSelect: () => void
}

const IconPickerGridCell = React.forwardRef<
  HTMLButtonElement,
  IconPickerGridCellProps
>(({ icon, icons, id, isActive, onSelect }, ref) => (
  // aria-selected marks the active descendant (role=button doesn't support it,
  // so it lives on the gridcell); the tint follows the same data-active marker.
  <div role="gridcell" aria-selected={isActive}>
    <button
      ref={ref}
      id={id}
      type="button"
      className={styles.cell}
      data-active={isActive ? '' : undefined}
      // Focus stays on the search input; cells are reached virtually via
      // aria-activedescendant, so none of them are tab stops.
      tabIndex={-1}
      aria-label={icon}
      onClick={onSelect}
    >
      <PickerIcon icon={icon} icons={icons} size={18} />
    </button>
  </div>
))

IconPickerGridCell.displayName = 'IconPicker.GridCell'

/**
 * Keyboard-navigable icon grid. Renders a skeleton until the lazy chunk loads.
 */
type IconPickerGridProps = React.HTMLAttributes<HTMLDivElement>

const IconPickerGrid = React.forwardRef<HTMLDivElement, IconPickerGridProps>(
  ({ className, ...props }, ref) => {
    const {
      value,
      icons,
      filteredIcons,
      columns,
      selectIcon,
      activeIndex,
      gridId,
      cellId,
      hasNavigatedRef,
    } = useIconPickerContext()
    const gridStyle: GridCssProperties = { '--icon-picker-columns': columns }
    const activeCellRef = React.useRef<HTMLButtonElement>(null)

    // Focus stays on the search input, so the browser won't auto-scroll the
    // active cell into view — do it here, but only in response to keyboard
    // navigation. Skipping the on-open reposition avoids scrolling the page
    // when the picker first appears.
    React.useEffect(() => {
      if (!hasNavigatedRef.current) return
      activeCellRef.current?.scrollIntoView({ block: 'nearest' })
    }, [activeIndex, hasNavigatedRef])

    if (!icons) {
      return (
        <div
          className={clsx(styles.grid, styles.gridSkeleton, className)}
          data-testid="icon-picker-skeleton"
          aria-hidden
        />
      )
    }

    // The grid never takes DOM focus: the search input owns the keyboard and
    // moves a virtual active cell (see the root's `handleSearchKeyDown`).
    return (
      <div
        ref={ref}
        id={gridId}
        role="grid"
        aria-label="Icons"
        data-color={value.color}
        className={clsx(styles.grid, tintClassName(value.color), className)}
        style={gridStyle}
        {...props}
      >
        {chunk(filteredIcons, columns).map((rowIcons, rowIndex) => (
          // Rows are positional; keying by index keeps them stable as the
          // filtered set changes and only the cells inside reconcile.
          <div role="row" className={styles.row} key={rowIndex}>
            {rowIcons.map((icon, columnIndex) => {
              const index = rowIndex * columns + columnIndex
              return (
                <IconPickerGridCell
                  key={icon}
                  ref={index === activeIndex ? activeCellRef : undefined}
                  id={cellId(index)}
                  icon={icon}
                  icons={icons}
                  isActive={index === activeIndex}
                  onSelect={() => selectIcon(icon)}
                />
              )
            })}
          </div>
        ))}
      </div>
    )
  },
)

IconPickerGrid.displayName = 'IconPicker.Grid'

/**
 * Message shown when the search filters out every icon.
 */
type IconPickerEmptyProps = React.HTMLAttributes<HTMLDivElement>

const IconPickerEmpty = React.forwardRef<HTMLDivElement, IconPickerEmptyProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={clsx(styles.empty, className)} {...props}>
      {children ?? 'No icons found.'}
    </div>
  ),
)

IconPickerEmpty.displayName = 'IconPicker.Empty'

/**
 * Shown when the lazy icon chunk fails to load. Offers a retry that re-attempts
 * the import instead of leaving the grid stuck on the loading skeleton.
 */
type IconPickerErrorProps = React.HTMLAttributes<HTMLDivElement>

const IconPickerError = React.forwardRef<HTMLDivElement, IconPickerErrorProps>(
  ({ className, ...props }, ref) => {
    const { retryLoadIcons } = useIconPickerContext()

    return (
      <div
        ref={ref}
        role="alert"
        className={clsx(styles.error, className)}
        {...props}
      >
        <span>Couldn&rsquo;t load icons.</span>
        <button
          type="button"
          className={styles.retryButton}
          onClick={retryLoadIcons}
        >
          Try again
        </button>
      </div>
    )
  },
)

IconPickerError.displayName = 'IconPicker.Error'

const IconPickerObject = Object.assign(IconPicker, {
  Trigger: IconPickerTrigger,
  Content: IconPickerContent,
  Search: IconPickerSearch,
  Swatches: IconPickerSwatches,
  Shuffle: IconPickerShuffle,
  Grid: IconPickerGrid,
  Empty: IconPickerEmpty,
  Error: IconPickerError,
})

export {
  IconPickerObject as IconPicker,
  useIconPickerContext,
  DEFAULT_ICON_PICKER_VALUE,
}
export type {
  IconPickerProps,
  IconPickerValue,
  IconPickerTriggerProps,
  IconPickerContentProps,
  IconPickerSearchProps,
  IconPickerSwatchesProps,
  IconPickerShuffleProps,
  IconPickerGridProps,
  IconPickerEmptyProps,
  IconPickerErrorProps,
}
